import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useRouter } from 'src/routes/hooks';

import { useAuthContext } from 'src/auth/hooks';
import { ResetPassword } from 'src/services/user/user-service-calls';

import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Inicio',
    linkTo: '/',
  },
  {
    label: 'Reestablecer contraseña',
    action: 'resetPassword',
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter();

  const { user } = useAuthContext();

  const { logout } = useAuthContext();

  const { enqueueSnackbar } = useSnackbar();

  const popover = usePopover();

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
      router.replace('/');
    } catch (error) {
      console.error(error);
      enqueueSnackbar('¡No se pudo cerrar la sesión!', { variant: 'error' });
    }
  };

  const handleResetPassword = async () => {
    try {
      popover.onClose();
      await ResetPassword();
      enqueueSnackbar('Contraseña reestablecida exitosamente', { variant: 'success' });
      window.location.reload();
    } catch (error) {
      console.error('Error resetting password:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al reestablecer la contraseña', {
        variant: 'error',
      });
    }
  };

  const handleClickItem = (option) => {
    if (option.action === 'resetPassword') {
      handleResetPassword();
    } else if (option.linkTo) {
      popover.onClose();
      router.push(option.linkTo);
    }
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={user?.photoURL}
          alt={user?.username}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {user?.username?.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          {(user?.employee != null || user?.doctor != null) ? (
            <>
              <Typography variant="subtitle2" noWrap>
                {user?.employee != null
                  ? `${user.employee.name} ${user.employee.lastName}`
                  : `${user.doctor.name} ${user.doctor.lastName}`}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Código Usuario:{' '}
                {user?.employee != null ? user.employee.sequence : user.doctor.sequence}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="subtitle2" noWrap>
                {user?.userInfo?.givenName}
              </Typography>
              <Typography variant="subtitle2">{user?.userInfo?.surname}</Typography>
            </>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option)}>
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Desconectarse
        </MenuItem>
      </CustomPopover>
    </>
  );
}
